import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Auth from './components/Auth';
import ScoreBoard from './components/ScoreBoard';
import UserProfile from './components/UserProfile';
import TeamDetail from './components/TeamDetail';
import PlayerDetail from './components/PlayerDetail';
import SharedMatch from './components/SharedMatch';

const CACHED_USER_KEY = 'volleyball_user';

function readCachedUser() {
  try {
    const cached = localStorage.getItem(CACHED_USER_KEY);
    return cached ? JSON.parse(cached) : null;
  } catch (error) {
    return null;
  }
}

function cacheUser(nextUser) {
  try {
    if (nextUser) {
      localStorage.setItem(CACHED_USER_KEY, JSON.stringify(nextUser));
    } else {
      localStorage.removeItem(CACHED_USER_KEY);
    }
  } catch (error) {
    // Non-fatal; in-memory user is enough for this session.
  }
}

function App() {
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [user, setUser] = useState(() => readCachedUser());
  const [showProfile, setShowProfile] = useState(false);
  const [nightMode, setNightMode] = useState(localStorage.getItem('nightMode') === 'true');
  const [isGuestMode, setIsGuestMode] = useState(false);
  const [currentView, setCurrentView] = useState('scoreboard'); // 'scoreboard', 'profile', 'team-detail', 'player-detail', 'shared-match', 'match-view'
  const [selectedTeamName, setSelectedTeamName] = useState(null);
  const [selectedTeamId, setSelectedTeamId] = useState(null);
  const [selectedPlayerId, setSelectedPlayerId] = useState(null);
  const [shareId, setShareId] = useState(null);
  const [selectedMatch, setSelectedMatch] = useState(null);
  const [teamDateRange, setTeamDateRange] = useState(null);

  useEffect(() => {
    const fetchUser = async () => {
      if (!token) return;
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/auth/user`, {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 8000
        });
        setUser(response.data);
        cacheUser(response.data);
      } catch (error) {
        console.error('Error fetching user:', error);
        // Only sign out on an explicit auth rejection. Network failures must
        // keep the cached session so scoring still works in a dead zone.
        if (error.response?.status === 401) {
          handleLogout();
        }
      }
    };
    fetchUser();
  }, [token]);

  // Check for shared match URLs
  useEffect(() => {
    const path = window.location.pathname;
    const shareMatch = path.match(/^\/share\/([a-f0-9]+)$/);
    
    if (shareMatch) {
      setShareId(shareMatch[1]);
      setCurrentView('shared-match');
    }
  }, []);

  useEffect(() => {
    document.body.classList.toggle('night-mode', nightMode);
    localStorage.setItem('nightMode', nightMode);
  }, [nightMode]);

  const handleLogout = () => {
    localStorage.removeItem('token');
    cacheUser(null);
    setToken(null);
    setUser(null);
    setShowProfile(false);
    setIsGuestMode(false);
    setCurrentView('scoreboard');
    setSelectedTeamName(null);
    setTeamDateRange(null);
    setSelectedTeamId(null);
    setSelectedPlayerId(null);
  };

  const handleGuestMode = () => {
    setIsGuestMode(true);
    setUser({ name: 'Guest', username: 'guest' });
  };

  const toggleProfile = () => {
    setShowProfile(!showProfile);
    setCurrentView(showProfile ? 'scoreboard' : 'profile');
  };

  const toggleNightMode = () => {
    setNightMode(!nightMode);
  };

  const handleTeamClick = (teamName, range) => {
    setSelectedTeamName(teamName);
    setTeamDateRange(range || null);
    setCurrentView('team-detail');
  };

  const handlePlayerClick = (teamId, playerId) => {
    setSelectedTeamId(teamId);
    setSelectedPlayerId(playerId);
    setCurrentView('player-detail');
  };

  const handleBackToScoreboard = () => {
    setCurrentView('scoreboard');
    setSelectedTeamName(null);
    setTeamDateRange(null);
    setSelectedTeamId(null);
    setSelectedPlayerId(null);
    setShowProfile(false);
  };

  const handleBackToTeamDetail = () => {
    setCurrentView('team-detail');
    setSelectedTeamId(null);
    setSelectedPlayerId(null);
  };

  const handleMatchClick = (match) => {
    setSelectedMatch(match);
    setCurrentView('match-view');
  };

  const handleBackFromMatchView = () => {
    setSelectedMatch(null);
    setCurrentView('scoreboard');
  };

  const handleEditMatch = (match) => {
    setSelectedMatch(match);
    setCurrentView('scoreboard'); // Go back to scoreboard to edit
    // The ScoreBoard component will handle the actual editing
  };

  const renderCurrentView = () => {
    // Handle shared match view first (doesn't require authentication)
    if (currentView === 'shared-match') {
      return (
        <SharedMatch 
          shareId={shareId}
          nightMode={nightMode}
        />
      );
    }

    if (!token && !isGuestMode) {
      return (
        <Auth 
          setToken={setToken} 
          setUser={setUser} 
          nightMode={nightMode} 
          toggleNightMode={toggleNightMode}
          onGuestMode={handleGuestMode}
        />
      );
    }

    switch (currentView) {
      case 'profile':
        return (
          <UserProfile 
            token={token} 
            onLogout={handleLogout} 
            onReturnToScoreboard={handleBackToScoreboard} 
            nightMode={nightMode}
            toggleNightMode={toggleNightMode}
          />
        );
      
      case 'team-detail':
        return (
          <TeamDetail 
            token={token}
            teamName={selectedTeamName}
            nightMode={nightMode}
            onBack={handleBackToScoreboard}
            onPlayerClick={handlePlayerClick}
            initialRange={teamDateRange}
          />
        );
      
      case 'player-detail':
        return (
          <PlayerDetail 
            token={token}
            teamId={selectedTeamId}
            playerId={selectedPlayerId}
            nightMode={nightMode}
            onBack={handleBackToTeamDetail}
            onTeamClick={handleTeamClick}
          />
        );
      
      case 'match-view':
        return (
          <SharedMatch 
            match={selectedMatch}
            nightMode={nightMode}
            showBackButton={true}
            onBack={handleBackFromMatchView}
            isAuthenticated={true}
            onEdit={handleEditMatch}
          />
        );
      
      default: // 'scoreboard'
        return (
          <ScoreBoard 
            token={token} 
            user={user} 
            onLogout={handleLogout} 
            onProfileClick={isGuestMode ? null : toggleProfile} 
            nightMode={nightMode}
            toggleNightMode={toggleNightMode}
            isGuestMode={isGuestMode}
            onTeamClick={handleTeamClick}
            onMatchClick={handleMatchClick}
            editMatch={selectedMatch}
            onEditComplete={() => setSelectedMatch(null)}
          />
        );
    }
  };

  return (
    <div className={`App ${nightMode ? 'night-mode' : ''}`}>
      {renderCurrentView()}
    </div>
  );
}

export default App;